import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Upload,
  FileText,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  CloudUpload,
  Shield,
  Clock,
  File,
  Mail,
  Info
} from 'lucide-react';
import api, { uploadFiles as apiUploadFiles } from '../utils/api';

// File size limits
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const RECOMMENDED_EMAIL_FILES = 5; // Show portal recommendation above this

// Document type icons
const FILE_ICONS: Record<string, React.ReactNode> = {
  'application/pdf': <FileText className="w-5 h-5 text-red-500" />,
  'text/csv': <FileSpreadsheet className="w-5 h-5 text-green-500" />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileSpreadsheet className="w-5 h-5 text-emerald-500" />,
  'application/vnd.ms-excel': <FileSpreadsheet className="w-5 h-5 text-emerald-500" />,
};

interface MissingDoc {
  name: string;
  reason: string;
}

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  docType?: string;
  sizeError?: boolean;
}

interface ClaimInfo {
  claimant_name: string;
  business_type: string;
  event_type: string;
  status: string;
  missing_docs: MissingDoc[];
  inbound_email?: string;
}

export default function ClientUploadPortal() {
  const { reportId } = useParams<{ reportId: string }>();
  const [claimInfo, setClaimInfo] = useState<ClaimInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // Generate inbound email address - using updates.claimsync.io domain
  const inboundEmail = `docs+${reportId}@updates.claimsync.io`;

  useEffect(() => {
    fetchClaimInfo();
  }, [reportId]);

  const fetchClaimInfo = async () => {
    try {
      setLoading(true);
      // Public endpoint - use centralized API utility
      const data = await api.get(`/api/v1/notifications/upload-portal/${reportId}`, { showErrorToast: false });
      setClaimInfo(data);
    } catch (err: any) {
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        setError('This upload link is invalid or has expired.');
      } else {
        setError(err.message || 'Failed to load claim information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validTypes = [
      'application/pdf',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const uploadFiles: UploadedFile[] = newFiles
      .filter(file => validTypes.includes(file.type) || file.name.endsWith('.csv'))
      .map(file => ({
        file,
        status: 'pending' as const,
        docType: guessDocType(file.name),
        sizeError: file.size > MAX_FILE_SIZE_BYTES
      }));

    // Track upload analytics
    trackUploadEvent('files_added', {
      count: uploadFiles.length,
      totalSize: uploadFiles.reduce((sum, f) => sum + f.file.size, 0),
      oversizedCount: uploadFiles.filter(f => f.sizeError).length
    });

    setFiles(prev => [...prev, ...uploadFiles]);
  };

  // Analytics tracking helper - fire and forget, no error handling needed
  const trackUploadEvent = async (event: string, data: Record<string, any>) => {
    try {
      await api.post('/api/v1/notifications/track-upload-analytics', {
        report_id: reportId,
        event,
        data,
        timestamp: new Date().toISOString()
      }, { showErrorToast: false, logError: false });
    } catch (err) {
      // Silent fail - analytics shouldn't break UX
      console.debug('Analytics tracking failed:', err);
    }
  };

  const guessDocType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.includes('1065') || lower.includes('tax') || lower.includes('return')) return 'Tax Returns';
    if (lower.includes('k-1') || lower.includes('k1')) return 'K-1 Forms';
    if (lower.includes('bank') || lower.includes('statement')) return 'Bank Statements';
    if (lower.includes('p&l') || lower.includes('pnl') || lower.includes('profit')) return 'P&L Statements';
    if (lower.includes('sales') || lower.includes('revenue')) return 'Sales Reports';
    if (lower.includes('payroll')) return 'Payroll Records';
    if (lower.includes('invoice')) return 'Invoices';
    return 'Financial Document';
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    
    // Filter out oversized files
    const validFiles = files.filter(f => !f.sizeError);
    if (validFiles.length === 0) {
      setError('All selected files exceed the size limit. Please reduce file sizes.');
      return;
    }

    setUploading(true);
    trackUploadEvent('upload_started', { fileCount: validFiles.length });
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== 'pending' || files[i].sizeError) continue;
      
      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' as const } : f
      ));
      
      try {
        const formData = new FormData();
        formData.append('file', files[i].file);
        formData.append('doc_type', files[i].docType || 'unknown');
        
        // Use centralized API utility for file upload
        await apiUploadFiles(`/api/v1/notifications/upload-document/${reportId}`, formData, { showErrorToast: false });
        
        // Update status to success
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'success' as const } : f
        ));
        successCount++;
      } catch (err) {
        // Update status to error
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error' as const, error: 'Upload failed' } : f
        ));
        errorCount++;
      }
    }
    
    setUploading(false);
    
    // Track completion
    trackUploadEvent('upload_completed', { successCount, errorCount });
    
    // Check if all uploads succeeded
    if (successCount > 0 && errorCount === 0) {
      setUploadComplete(true);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading upload portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Upload Link Invalid</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">
            Please contact your account representative for a new upload link.
          </p>
        </div>
      </div>
    );
  }

  if (uploadComplete) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Upload Complete!</h1>
          <p className="text-slate-600 mb-6">
            Thank you for uploading your documents. Our team will review them and update your claim analysis.
          </p>
          <p className="text-sm text-slate-500">
            You will receive an email once your documents have been processed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-900 to-blue-600 text-white py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">ClaimSync</h1>
          <p className="text-blue-100">Secure Document Upload Portal</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Claim Info Card */}
        {claimInfo && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Claim Information
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Claimant</p>
                <p className="font-medium text-slate-900">{claimInfo.claimant_name}</p>
              </div>
              <div>
                <p className="text-slate-500">Business Type</p>
                <p className="font-medium text-slate-900">{claimInfo.business_type}</p>
              </div>
              <div>
                <p className="text-slate-500">Event Type</p>
                <p className="font-medium text-slate-900">{claimInfo.event_type}</p>
              </div>
              <div>
                <p className="text-slate-500">Status</p>
                <p className="font-medium text-slate-900">{claimInfo.status}</p>
              </div>
            </div>
          </div>
        )}

        {/* Missing Documents */}
        {claimInfo?.missing_docs && claimInfo.missing_docs.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Documents Needed ({claimInfo.missing_docs.length})
            </h3>
            <div className="space-y-3">
              {claimInfo.missing_docs.map((doc, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2" />
                  <div>
                    <p className="font-medium text-slate-900">{doc.name}</p>
                    <p className="text-sm text-slate-600">{doc.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRIMARY: Upload Area - Emphasized as main action */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-blue-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Upload Your Documents
            </h3>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              ✓ Recommended
            </span>
          </div>

          {/* File size guidance */}
          <div className="flex items-start gap-2 mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Best for multiple or large files</p>
              <p className="text-blue-600">Files up to {MAX_FILE_SIZE_MB}MB each • PDF, CSV, Excel supported</p>
            </div>
          </div>

          {/* Drag & Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-xl p-8 text-center transition-colors
              ${isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-300 hover:border-slate-400'
              }
            `}
          >
            <CloudUpload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
            <p className="text-slate-600 mb-2">
              Drag and drop your files here, or
            </p>
            <label className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition font-medium">
              <Upload className="w-5 h-5" />
              Browse Files
              <input
                type="file"
                multiple
                accept=".pdf,.csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              <h4 className="font-medium text-slate-900">Selected Files ({files.length})</h4>
              {files.map((file, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    file.sizeError ? 'bg-red-50 border border-red-200' : 'bg-slate-50'
                  }`}
                >
                  {FILE_ICONS[file.file.type] || <File className="w-5 h-5 text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${file.sizeError ? 'text-red-700' : 'text-slate-900'}`}>
                      {file.file.name}
                    </p>
                    <p className={`text-xs ${file.sizeError ? 'text-red-600' : 'text-slate-500'}`}>
                      {formatFileSize(file.file.size)} • {file.docType}
                      {file.sizeError && ` • Exceeds ${MAX_FILE_SIZE_MB}MB limit`}
                    </p>
                  </div>
                  {file.status === 'pending' && !file.sizeError && (
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 text-slate-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {file.sizeError && (
                    <div className="flex items-center gap-1">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {file.status === 'uploading' && (
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  )}
                  {file.status === 'success' && (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                  {file.status === 'error' && !file.sizeError && (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              ))}

              {/* Size error warning */}
              {files.some(f => f.sizeError) && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Some files exceed the {MAX_FILE_SIZE_MB}MB limit. Please reduce their size or split into smaller files.
                  </span>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={uploadFiles}
                disabled={uploading || files.every(f => f.status !== 'pending' || f.sizeError)}
                className="w-full mt-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudUpload className="w-5 h-5" />
                    Upload All Documents
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* SECONDARY: Email Option - Fallback */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-5 h-5 text-slate-500" />
            <h3 className="font-semibold text-slate-700">
              Alternative: Email Your Documents
            </h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            For quick, single-file submissions only. For 5+ files or large documents, 
            please use the secure upload portal above for faster processing.
          </p>
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200">
            <span className="text-blue-600 font-mono text-sm flex-1 truncate">
              📧 {inboundEmail}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(inboundEmail);
                setEmailCopied(true);
                trackUploadEvent('email_copied', {});
                setTimeout(() => setEmailCopied(false), 2000);
              }}
              className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 rounded text-sm font-medium transition"
            >
              {emailCopied ? '✓ Copied!' : 'Copy'}
            </button>
            <a
              href={`mailto:${inboundEmail}?subject=Documents for Claim`}
              onClick={() => trackUploadEvent('email_opened', {})}
              className="px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded text-sm font-medium transition"
            >
              Open Email
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Documents sent here will be automatically attached to your claim.
          </p>
        </div>

        {/* Security Notice */}
        <div className="flex items-start gap-3 text-sm text-slate-600">
          <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p>
            Your documents are encrypted and transmitted securely. 
            Only authorized personnel can access your files.
          </p>
        </div>
      </div>
    </div>
  );
}
